import { Extension, type Editor, type Range } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import Suggestion, {
  type SuggestionOptions,
  type SuggestionProps,
} from '@tiptap/suggestion';
import {
  forwardRef,
  useImperativeHandle,
  useState,
  type ReactNode,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { ScreenshotWindowPickerModal } from '@/components/modals/ScreenshotWindowPickerModal';
import { isMac } from '@/utils/capabilities';

import { EditorCommandOut } from '../components/editor-command';
import { EditorCommandFloating } from '../components/editor-command-floating';

const Command = Extension.create({
  name: 'slash-command',
  addOptions() {
    return {
      suggestion: {
        char: '/',
        command: ({ editor, range, props }) => {
          props.command({ editor, range });
        },
      } as Omit<SuggestionOptions, 'editor'>,
    };
  },
  addProseMirrorPlugins() {
    return [
      Suggestion({
        ...this.options.suggestion,
        editor: this.editor,
      }),
    ];
  },
});

interface CommandFloatingWrapperProps {
  readonly query: string;
  readonly range: Range;
  readonly clientRect: (() => DOMRect | null) | null;
}

interface CommandFloatingWrapperRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const CommandFloatingWrapper = forwardRef<
  CommandFloatingWrapperRef,
  CommandFloatingWrapperProps
>(({ query, range, clientRect }, ref) => {
  const [show, setShow] = useState(true);

  useImperativeHandle(ref, () => ({
    onKeyDown: (props: { event: KeyboardEvent }) => {
      if (props.event.key === 'Escape') {
        setShow(false);
        return true;
      }
      return false;
    },
  }));

  return (
    <EditorCommandFloating show={show} getReferenceClientRect={clientRect}>
      <EditorCommandOut query={query} range={range} />
    </EditorCommandFloating>
  );
});

CommandFloatingWrapper.displayName = 'CommandFloatingWrapper';

const renderItems = () => {
  let component: ReactRenderer<CommandFloatingWrapperRef> | null = null;

  return {
    onStart: (props: SuggestionProps) => {
      const { selection } = props.editor.state;
      const parentNode = selection.$from.node(selection.$from.depth);
      const blockType = parentNode.type.name;

      if (blockType === 'codeBlock') {
        return false;
      }

      component = new ReactRenderer(CommandFloatingWrapper, {
        props: {
          query: props.query,
          range: props.range,
          clientRect: props.clientRect ?? null,
        },
        editor: props.editor,
      });
    },
    onUpdate: (props: SuggestionProps) => {
      component?.updateProps({
        clientRect: props.clientRect ?? null,
        query: props.query,
        range: props.range,
      });
    },

    onKeyDown: (props: { event: KeyboardEvent }) => {
      if (props.event.key === 'Escape') {
        return component?.ref?.onKeyDown(props) ?? false;
      }

      return false;
    },
    onExit: () => {
      component?.destroy();
      component = null;
    },
  };
};

export interface SuggestionItem {
  title: string;
  description: string;
  icon: ReactNode;
  searchTerms?: string[];
  command?: (props: { editor: Editor; range: Range }) => void;
}

/* eslint-disable react-refresh/only-export-components */
export const createSuggestionItems = (items: SuggestionItem[]) => items;

export function handleCommandNavigation(
  event: KeyboardEvent
): boolean | undefined {
  if (['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)) {
    const slashCommand = document.querySelector('#slash-command');
    if (slashCommand) {
      return true;
    }
  }
}

export { Command, renderItems };

/**
 * Retrieves the upload function from the ImageUploadExtension storage
 * and inserts the file into the editor via the upload pipeline
 * (placeholder decoration, upload, then real node with fileId).
 */
function insertImageViaUploadPipeline(editor: Editor, file: File) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uploadFn = (editor.storage as any).imageUpload?.uploadFn as
    | ((file: File, view: unknown, pos: number) => void)
    | undefined;
  if (uploadFn) {
    uploadFn(file, editor.view, editor.state.selection.from);
  }
}

const screenshotSuggestionItem: SuggestionItem = {
  title: 'Screenshot',
  description: 'Capture a window screenshot',
  searchTerms: ['screenshot', 'capture', 'window', 'screen'],
  icon: <Icons.camera size={18} />,
  command: ({ editor, range }) => {
    editor.chain().focus().deleteRange(range).run();
    ModalsManager.showResolvable(ScreenshotWindowPickerModal).then((file) => {
      if (file) insertImageViaUploadPipeline(editor, file);
    });
  },
};

const baseSuggestionItems: SuggestionItem[] = [
  {
    title: 'Text',
    description: 'Just start typing with plain text',
    searchTerms: ['p', 'paragraph'],
    icon: <Icons.text size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .toggleNode('paragraph', 'paragraph')
        .run();
    },
  },
  {
    title: 'To-do List',
    description: 'Track tasks with a to-do list',
    searchTerms: ['todo', 'task', 'list', 'check', 'checkbox'],
    icon: <Icons.todoList size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleTaskList().run();
    },
  },
  {
    title: 'Heading 1',
    description: 'Big section heading',
    searchTerms: ['title', 'big', 'large'],
    icon: <Icons.heading1 size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode('heading', { level: 1 })
        .run();
    },
  },
  {
    title: 'Heading 2',
    description: 'Medium section heading',
    searchTerms: ['subtitle', 'medium'],
    icon: <Icons.heading2 size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode('heading', { level: 2 })
        .run();
    },
  },
  {
    title: 'Heading 3',
    description: 'Small section heading',
    searchTerms: ['subtitle', 'small'],
    icon: <Icons.heading3 size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode('heading', { level: 3 })
        .run();
    },
  },
  {
    title: 'Bullet List',
    description: 'Create a simple bullet list',
    searchTerms: ['unordered', 'point'],
    icon: <Icons.bulletList size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: 'Numbered List',
    description: 'Create a list with numbering',
    searchTerms: ['ordered'],
    icon: <Icons.numList size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    title: 'Quote',
    description: 'Capture a quote',
    searchTerms: ['blockquote'],
    icon: <Icons.quote size={18} />,
    command: ({ editor, range }) =>
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .toggleNode('paragraph', 'paragraph')
        .toggleBlockquote()
        .run(),
  },
  {
    title: 'Code',
    description: 'Capture a code snippet',
    searchTerms: ['codeblock'],
    icon: <Icons.code size={18} />,
    command: ({ editor, range }) =>
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
  },
  {
    title: 'Table',
    description: 'Insert a table',
    searchTerms: ['table', 'grid', 'rows', 'columns'],
    icon: <Icons.table size={18} />,
    command: ({ editor, range }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },
  },
  {
    title: 'Image',
    description: 'Upload an image',
    searchTerms: ['image', 'picture', 'photo', 'upload'],
    icon: <Icons.imageFile size={18} />,
    command: ({ editor, range }) => {
      editor.chain().focus().deleteRange(range).run();
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = () => {
        const file = input.files?.[0];
        if (file) insertImageViaUploadPipeline(editor, file);
      };
      input.click();
    },
  },
];

export const defaultSuggestionItems = createSuggestionItems(
  isMac()
    ? [...baseSuggestionItems, screenshotSuggestionItem]
    : baseSuggestionItems
);

export const defaultSlashCommand = Command.configure({
  suggestion: {
    items: () => defaultSuggestionItems,
    render: renderItems,
  },
});

/* eslint-enable react-refresh/only-export-components */
